/**
 * CLEAR Talent's published list price, declared once.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * Three places published "USD 7.50 per employee per month" as the starting
 * price — `public/llms.txt`, the comparison model, and the buyer's-guide
 * calculator — while `pages/Pricing.tsx` published a structurally different
 * Basic plan: a flat monthly fee covering a fixed allocation of users, with
 * per-user add-ons above it. No $7.50 figure appears anywhere on that page.
 * `llms.txt` is served to the answer-engine crawlers `robots.txt` allows, so the
 * contradiction was being fed to LLMs as ground truth about what the product
 * costs.
 *
 * The pricing page is the commercial source of truth, so it is the number that
 * survives. Everything the marketing site says about price now derives from the
 * values below rather than restating a per-employee rate the product does not
 * sell. `pages/Pricing.tsx` renders its tiers from the same constants, so the
 * figure a buyer reads on /pricing and the figure the rest of the site publishes
 * cannot drift apart again.
 *
 * Changing a number here changes the pricing page, the comparison model, the
 * head-to-head pages and the buyer's-guide calculator together. The AI-readable
 * files in `public/` are static and cannot import from here; `seo/llmsTxt.test.ts`
 * holds them to these values instead.
 *
 * NOT COVERED HERE: the Ava chat system prompt in `frontend/api/ava-chat.ts` also
 * quotes the old $7.50 figure. It is a Vercel function outside this import graph
 * and needs the same correction.
 */

export interface PublishedTier {
  name: string;
  /** Flat monthly list price in USD, before the annual discount. */
  monthlyUsd: number;
  includedAdmins: number;
  includedStaff: number;
  /** Monthly list price of each staff user beyond the included allocation. */
  extraStaffUsd: number;
  /** Highest staff count the plan covers, as stated on the pricing page. */
  maxStaff: number;
  /** Monthly list price of each admin user beyond the included allocation. */
  extraAdminUsd: number;
  maxAdmins: number;
}

export const BASIC_TIER: PublishedTier = {
  name: 'Basic',
  monthlyUsd: 39,
  includedAdmins: 1,
  includedStaff: 10,
  extraStaffUsd: 3,
  maxStaff: 100,
  extraAdminUsd: 9,
  maxAdmins: 5,
};

export const PRO_TIER: PublishedTier = {
  name: 'Pro',
  monthlyUsd: 295,
  includedAdmins: 3,
  includedStaff: 50,
  extraStaffUsd: 5,
  maxStaff: 100,
  extraAdminUsd: 15,
  maxAdmins: 50,
};

/** The plans that carry a published price. Enterprise is quoted. */
export const PUBLISHED_TIERS: PublishedTier[] = [BASIC_TIER, PRO_TIER];

export const ANNUAL_DISCOUNT_PERCENT = 15;
export const TRIAL_POLICY = 'Free 1-month trial';
export const SETUP_FEES_POLICY = 'Custom / quoted depending on package';

/** The lowest published price on the site — the number "starting from" means. */
export const STARTING_MONTHLY_USD = BASIC_TIER.monthlyUsd;

/** Staff headcount past which no plan carries a published price. */
export const PUBLISHED_STAFF_CAP = Math.max(...PUBLISHED_TIERS.map((tier) => tier.maxStaff));

/**
 * Monthly list price of `tier` for a team of `staff` staff users on the plan's
 * included admin allocation, or null when the plan does not reach that headcount.
 */
export const monthlyListPrice = (tier: PublishedTier, staff: number): number | null => {
  if (staff > tier.maxStaff) return null;
  return tier.monthlyUsd + Math.max(0, staff - tier.includedStaff) * tier.extraStaffUsd;
};

/**
 * The cheapest published monthly list price for `staff` staff users, or null once
 * the headcount is past every published plan and Enterprise has to be quoted.
 * Plans differ in capability as well as price, so this answers "what does the
 * published price list cover" — not "which plan should we buy".
 */
export const lowestMonthlyListPrice = (staff: number): number | null => {
  const prices = PUBLISHED_TIERS
    .map((tier) => monthlyListPrice(tier, staff))
    .filter((price): price is number => price !== null);

  return prices.length > 0 ? Math.min(...prices) : null;
};

/** Price with the annual commitment applied, rounded as the pricing page rounds it. */
export const annualBilledMonthlyPrice = (monthlyUsd: number) =>
  Math.round(monthlyUsd * (1 - ANNUAL_DISCOUNT_PERCENT / 100));

/**
 * What an annual commitment is invoiced at for the year. Rounded from the
 * unrounded discounted price, as the pricing page has always shown it, so it is
 * not simply twelve times `annualBilledMonthlyPrice`.
 */
export const annualBilledYearlyPrice = (monthlyUsd: number) =>
  Math.round(monthlyUsd * (1 - ANNUAL_DISCOUNT_PERCENT / 100) * 12);

/**
 * What the Enterprise plan adds on top of Pro, as the pricing page lists it.
 * Declared here so the plan card and the FAQ answer about Enterprise read from
 * the same list.
 */
export const ENTERPRISE_ONLY_MODULES = [
  'Talent & Succession Planning',
  'Compensation Management',
  'Attrition Risk Prediction',
  'Performance Risk Prediction',
  'Executive Narrative Summaries',
];

export const ENTERPRISE_ENHANCEMENTS = [
  'White-label customization',
  'Custom AI models / tuning',
  'Enterprise contracts',
  'Advanced compliance controls',
];

/**
 * The date the /pricing page's visible content last changed — the sitemap
 * `<lastmod>` for the route (see `routeLastModified` in `entry-server.tsx`).
 * Move it forward whenever a figure, a plan or an FAQ answer on the page changes,
 * and not otherwise: a lastmod that moves without the page changing teaches
 * search engines to ignore it.
 */
export const PRICING_LAST_MODIFIED = '2026-09-24';

export interface PricingFaq {
  question: string;
  answer: string;
}

const usd = (value: number) => `$${value.toLocaleString('en-US')}`;

/**
 * The FAQ list /pricing renders, and the FAQPage node `seo/siteSeo.ts` emits for
 * the route. One list for both, so the markup can never answer a question
 * differently from the page a visitor reads.
 *
 * Nothing here commits the business to a discount, a support tier or a service
 * level that exists nowhere else on the site. A published price is a promise;
 * "30% off for non-profits" and "Enterprise customers get a dedicated success
 * manager" were exactly that, invented in the page alone, and they are why the
 * page once sat unpublished. Add a commitment to this file first — or leave it
 * for the sales conversation.
 */
export const PRICING_FAQS: PricingFaq[] = [
  {
    question: 'How does the free trial work?',
    answer: `${TRIAL_POLICY} - no credit card required. You'll have full access to all features of your chosen plan during the trial period. After the trial, you can choose to continue with a paid subscription.`,
  },
  {
    question: 'How much do additional users cost?',
    answer: `Each plan includes a set number of users, and you add more at a fixed monthly rate per user. On Basic, which includes ${BASIC_TIER.includedAdmins} admin user and ${BASIC_TIER.includedStaff} staff users, each additional staff user is ${usd(BASIC_TIER.extraStaffUsd)} a month (up to ${BASIC_TIER.maxStaff} staff users) and each additional admin user is ${usd(BASIC_TIER.extraAdminUsd)} a month (up to ${BASIC_TIER.maxAdmins}). On Pro, which includes ${PRO_TIER.includedAdmins} admin users and ${PRO_TIER.includedStaff} staff users, additional staff users are ${usd(PRO_TIER.extraStaffUsd)} a month (up to ${PRO_TIER.maxStaff}) and additional admin users are ${usd(PRO_TIER.extraAdminUsd)} a month (up to ${PRO_TIER.maxAdmins}). Enterprise user limits and rates are quoted.`,
  },
  {
    question: 'What is the difference between monthly and annual billing?',
    answer: `Paying annually saves ${ANNUAL_DISCOUNT_PERCENT}% on the plan price. Basic is ${usd(BASIC_TIER.monthlyUsd)} a month billed monthly, or ${usd(annualBilledMonthlyPrice(BASIC_TIER.monthlyUsd))} a month billed annually at ${usd(annualBilledYearlyPrice(BASIC_TIER.monthlyUsd))} a year. Pro is ${usd(PRO_TIER.monthlyUsd)} a month billed monthly, or ${usd(annualBilledMonthlyPrice(PRO_TIER.monthlyUsd))} a month billed annually at ${usd(annualBilledYearlyPrice(PRO_TIER.monthlyUsd))} a year.`,
  },
  {
    question: 'What does the Enterprise plan add?',
    answer: `Enterprise includes everything in Pro, plus modules and AI capabilities only it carries: ${ENTERPRISE_ONLY_MODULES.join(', ')}. It also adds ${ENTERPRISE_ENHANCEMENTS.map((item) => item.toLowerCase()).join(', ')}. Pricing and user limits are quoted to your organization's scope rather than published.`,
  },
  {
    question: 'Can I change plans later?',
    answer: 'Yes, you can upgrade or downgrade your plan at any time. When upgrading, the new features become available immediately. When downgrading, the change takes effect at the start of your next billing period.',
  },
  {
    question: 'Are there any setup fees?',
    answer: `${SETUP_FEES_POLICY}. Our team will work with you to determine the best implementation approach for your organization's needs.`,
  },
  {
    question: 'Is my data secure?',
    answer: 'Absolutely. We\'ve completed a comprehensive enterprise security hardening of our platform. Your data is protected with end-to-end TLS encryption across all services, time-limited signed file URLs that auto-expire after 24 hours, and 24/7 infrastructure threat monitoring via Google Cloud Security Command Center Premium. Our platform has earned an A+ rating from Mozilla Observatory — the highest possible score for web security. We run on Google Cloud Platform with global data centres for localized storage and regional compliance. Every operation is audit-logged and all access follows least-privilege principles.',
  },
  {
    question: 'Can I export my data?',
    answer: 'Yes. Every plan includes data export, and records and reports can be taken out as CSV, Excel, or PDF.',
  },
  {
    question: 'Do you offer implementation support?',
    answer: 'All plans include our help center and email support. Implementation is scoped with you rather than sold as a fixed package — tell our team your rollout timetable and the shape of your organization, and we will confirm what onboarding involves before you commit.',
  },
];

/** Short label for a table cell or a card — no room for the plan's conditions. */
export const STARTING_PRICE_LABEL = `From $${STARTING_MONTHLY_USD} per month`;

/**
 * One sentence for prose, wherever the site states its own price. Spelled so it
 * reads correctly in the American-English marketing pages and the British-English
 * head-to-head pages alike.
 */
export const STARTING_PRICE_SENTENCE =
  `CLEAR Talent publishes a list price rather than quoting from scratch: the Basic plan is USD ${BASIC_TIER.monthlyUsd} per month and covers ${BASIC_TIER.includedAdmins} admin user and ${BASIC_TIER.includedStaff} staff users, with each additional staff user at USD ${BASIC_TIER.extraStaffUsd} per month and each additional admin user at USD ${BASIC_TIER.extraAdminUsd}, up to the maximums stated on the plan. The Pro plan is USD ${PRO_TIER.monthlyUsd} per month and covers ${PRO_TIER.includedAdmins} admin users and ${PRO_TIER.includedStaff} staff users. Past those limits the Enterprise plan is quoted.`;
